/** Utilitários de documentos brasileiros (CPF e CNPJ, inclusive o CNPJ alfanumérico de 2026). */

export const onlyDigits = (v: string | null | undefined) => String(v ?? '').replace(/\D/g, '');

/** Remove pontuação mantendo letras (CNPJ alfanumérico). */
export const normalizeDocument = (v: string | null | undefined) =>
  String(v ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');

export function isValidCPF(value: string): boolean {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const digit = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(cpf[i]) * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return digit(9) === Number(cpf[9]) && digit(10) === Number(cpf[10]);
}

/**
 * CNPJ numérico ou alfanumérico (IN RFB 2.229/2024): os 12 primeiros caracteres podem ser
 * [0-9A-Z]; cada caractere vale (código ASCII − 48); os dígitos verificadores continuam numéricos.
 */
export function isValidCNPJ(value: string): boolean {
  const cnpj = normalizeDocument(value);
  if (!/^[0-9A-Z]{12}\d{2}$/.test(cnpj)) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;
  const values = cnpj.split('').map((c) => c.charCodeAt(0) - 48);
  const calc = (len: number) => {
    const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((acc, w, i) => acc + w * values[i], 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return calc(12) === values[12] && calc(13) === values[13];
}

export function isValidDocument(value: string): boolean {
  const doc = normalizeDocument(value);
  if (doc.length === 11) return isValidCPF(doc);
  if (doc.length === 14) return isValidCNPJ(doc);
  return false;
}

export function formatDocument(value: string | null | undefined): string {
  const doc = normalizeDocument(value);
  if (doc.length === 11) return doc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  if (doc.length === 14) return doc.replace(/^(\w{2})(\w{3})(\w{3})(\w{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return String(value ?? '');
}

export const BRAZIL_UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
  'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
] as const;

export function formatZip(value: string | null | undefined): string {
  const d = onlyDigits(value).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/**
 * Máscara progressiva enquanto digita: até 11 caracteres vira CPF (000.000.000-00),
 * acima disso CNPJ (00.000.000/0000-00, aceitando o CNPJ alfanumérico).
 */
export function maskDocumentBR(value: string | null | undefined): string {
  const raw = normalizeDocument(value).slice(0, 14);
  if (!raw) return '';
  if (raw.length <= 11 && /^\d*$/.test(raw)) {
    const p = [raw.slice(0, 3), raw.slice(3, 6), raw.slice(6, 9), raw.slice(9, 11)];
    let out = p[0];
    if (p[1]) out += `.${p[1]}`;
    if (p[2]) out += `.${p[2]}`;
    if (p[3]) out += `-${p[3]}`;
    return out;
  }
  const p = [raw.slice(0, 2), raw.slice(2, 5), raw.slice(5, 8), raw.slice(8, 12), raw.slice(12, 14)];
  let out = p[0];
  if (p[1]) out += `.${p[1]}`;
  if (p[2]) out += `.${p[2]}`;
  if (p[3]) out += `/${p[3]}`;
  if (p[4]) out += `-${p[4]}`;
  return out;
}
